import { Link, NavLink, useNavigate } from "react-router-dom";
import { CalendarCog, Coins, FileSearch, Gauge, LogOut, Plus, User, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="app-logo">
      <span className="w-8 h-8 grid place-items-center bg-[#E50914] rounded-sm">
        <Wrench size={17} strokeWidth={2.5} />
      </span>
      <span className="font-display text-xl font-extrabold tracking-wide uppercase">
        AutoTech<span className="text-[#E50914]">//</span>Pro
      </span>
    </Link>
  );
}

export function CreditsBadge({ user }) {
  return (
    <Link
      to="/crediti"
      data-testid="credits-counter-badge"
      className="flex items-center gap-2 surface-2 px-3 py-1.5 hover:border-[#E50914] transition-colors"
    >
      <Coins size={15} className="text-[#E50914]" />
      <span className="mono text-sm font-bold" data-testid="credits-count">{user.credits}</span>
      <span className="text-xs text-slate-400 hidden sm:inline">crediti</span>
      {user.subscription_active && <span className="tag text-[#4ADE80] !border-green-500/30">ABBON.</span>}
    </Link>
  );
}

function navItems(role) {
  if (role === "admin") {
    return [
      { to: "/admin", label: "Coda", icon: Gauge, end: true },
      { to: "/admin/calendario", label: "Calendario", icon: CalendarCog },
      { to: "/admin/preventivi", label: "Preventivi", icon: FileSearch },
      { to: "/profilo", label: "Profilo", icon: User },
    ];
  }
  return [
    { to: "/dashboard", label: "Ticket", icon: Gauge },
    { to: "/nuova-richiesta", label: "Nuova", icon: Plus },
    { to: "/crediti", label: "Crediti", icon: Coins },
    { to: "/profilo", label: "Profilo", icon: User },
  ];
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navItems(user.role);
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors ${
      isActive ? "text-white bg-[#262626]" : "text-slate-400 hover:text-white"
    }`;
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header className="sticky top-0 z-40 bg-[#121212]/90 backdrop-blur-md border-b border-[#2a2a2a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
          <div className="flex items-center gap-3 min-w-0 shrink-0">
            <Logo />
            <span className="w-px h-7 bg-[#383838] shrink-0" />
            <span className="flex items-center gap-2" data-testid="appshell-dads-brand">
              <img src="/img/dads-logo-sm.png" alt="Dad's Garage Roots" className="h-8 w-[26px] object-contain shrink-0" />
              <span className="mono text-[10px] tracking-widest text-slate-400 uppercase hidden xl:inline whitespace-nowrap">by Dad's Garage Roots</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} className={linkCls} data-testid={`nav-${i.label.toLowerCase()}`}>
                <i.icon size={16} /> {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {user.role === "admin" ? (
              <span className="tag text-[#E50914] !border-red-500/40" data-testid="admin-role-badge">TECNICO</span>
            ) : (
              <CreditsBadge user={user} />
            )}
            <span className="hidden lg:block text-sm text-slate-300 max-w-[160px] truncate" data-testid="header-user-name">
              {user.name}
            </span>
            <button onClick={doLogout} className="btn-ghost p-2" title="Esci" data-testid="logout-button">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">{children}</main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#1A1A1A] border-t border-[#2a2a2a] grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-testid={`mobile-nav-${i.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] ${isActive ? "text-[#E50914]" : "text-slate-400"}`}>
            <i.icon size={19} /> {i.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
